import { NavLink, useLocation } from "react-router-dom";
import { NAV } from "@/config/nav";
import { useAuth } from "@/context/AuthContext";
import { StudioSwitcher } from "@/components/StudioSwitcher";
import { SignOut, X } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export function Sidebar({ mobileOpen, onClose }) {
  const { user, logout } = useAuth();
  const loc = useLocation();

  const content = (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-magic flex items-center justify-center font-display font-black text-black">M</div>
          <span className="font-display font-extrabold text-white text-sm tracking-tight">MY CHOICE STUDIOS</span>
        </div>
        <button onClick={onClose} className="lg:hidden text-zinc-400 hover:text-white" data-testid="sidebar-close">
          <X size={22} />
        </button>
      </div>
      <div className="px-3 pb-2 shrink-0">
        <StudioSwitcher onNavigate={onClose} />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-6">
        {NAV.map((group) => (
          <div key={group.section} className="mt-5">
            <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-600">{group.section}</div>
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = loc.pathname === item.to;
              return (
                <NavLink key={item.to} to={item.to} onClick={onClose}
                  data-testid={`nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
                  className={cn(
                    "group flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors",
                    active ? "bg-white/[0.06] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
                  )}>
                  <Icon size={19} weight={active ? "fill" : "duotone"}
                    className={cn(active ? "text-[#00E5FF]" : "text-zinc-500 group-hover:text-zinc-300")} />
                  <span className="truncate">{item.label}</span>
                  {active && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#00E5FF]" />}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-white/5 p-3">
        <div className="flex items-center gap-3 rounded-xl px-3 py-2">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#FF007F] flex items-center justify-center font-bold text-black text-sm">
            {(user?.name || "O")[0]}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-white truncate">{user?.name || "Owner"}</div>
            <div className="text-[11px] text-zinc-500 truncate">{user?.email}</div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="text-zinc-500 hover:text-[#FF3366] transition-colors">
            <SignOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden lg:block w-64 shrink-0 border-r border-white/5 bg-[#050505]">{content}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
          <div className="absolute left-0 top-0 h-full w-72 glass border-r border-white/10">{content}</div>
        </div>
      )}
    </>
  );
}
